import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { supabase } from '../../../../src/lib/supabase';
import { embedToArray } from '../../../../src/lib/utils';
import { useAuth } from '../../../../src/contexts/AuthContext';
import { dispararSinalModalDetalhe } from '../../../../src/lib/negotiationModalSignal';
import { Negociacao, UnidadeConsumidora } from '../../../../types';
import DetailHeader from './DetailHeader';
import DetailLeftColumn from './DetailLeftColumn';
import DetailFeed from './DetailFeed';
import DetailRightColumn from './DetailRightColumn';
import { propsFundoModal } from '../../../../src/lib/backdropModal';
import { TrocaDonoNegociacaoAviso } from '../../../../src/components/TrocaDonoAviso';
import IndicacaoNegociacaoCard from './IndicacaoNegociacaoCard';

interface NegotiationDetailModalProps {
    negotiationId: number | null;
    isOpen: boolean;
    onClose: () => void;
    onUpdate: () => void;
}

const NegotiationDetailModal: React.FC<NegotiationDetailModalProps> = ({ negotiationId, isOpen, onClose, onUpdate }) => {
    const { user } = useAuth();
    const [negotiation, setNegotiation] = useState<Negociacao | null>(null);
    const [ucs, setUcs] = useState<UnidadeConsumidora[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (isOpen && negotiationId) {
            fetchData();
        }
    }, [isOpen, negotiationId]);

    // Sinal global (LeadAssignedPopup.tsx e outros consumidores): este componente é
    // remontado em várias telas do app, então é ele quem avisa "tem modal de
    // detalhe aberto", não cada tela individualmente.
    useEffect(() => {
        dispararSinalModalDetalhe({ aberto: isOpen, negociacaoId: isOpen ? negotiationId : null });
        return () => dispararSinalModalDetalhe({ aberto: false, negociacaoId: null });
    }, [isOpen, negotiationId]);

    // Abrir o detalhe é uma das duas únicas ações que marcam o aviso de "lead
    // atribuído" como visto (a outra é "marcar tudo como visto" no popup) — só
    // fechar o popup sem abrir não pode desligar o pulso do card no Kanban.
    useEffect(() => {
        if (!isOpen || !negotiationId || !user) return;
        supabase
            .from('notifications')
            .update({ read: true })
            .eq('user_id', user.id)
            .eq('type', 'negotiation_assigned')
            .eq('read', false)
            .eq('metadata->>negociacao_id', String(negotiationId))
            .then(({ error }) => { if (error) console.error('Erro ao marcar aviso de lead como visto:', error); });
    }, [isOpen, negotiationId, user]);

    const fetchData = async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            // Negociação e UCs não dependem uma da outra — disparam juntas em vez de em
            // série. O perfil do responsável continua dependente (precisa do
            // responsavel_id que só vem na negociação), mas agora sobrepõe com o
            // round-trip das UCs em vez de rodar depois dele.
            const negPromise = supabase
                .from('negociacoes')
                .select('*, analise_cadastral(decisao_final), avalistas:negociacao_avalistas(*)')
                .eq('id', negotiationId)
                .single();
            const ucsPromise = supabase
                .from('unidades_consumidoras')
                .select('*')
                .eq('negociacao_id', negotiationId);

            // Fetch Negotiation
            const { data: negData, error: negError } = await negPromise;
            if (negError) throw negError;

            // Fetch Responsible Profile separately to avoid Join issues
            let responsavelProfile = undefined;
            if (negData.responsavel_id) {
                const { data: profileData } = await supabase
                    .from('profiles')
                    .select('*')
                    .eq('id', negData.responsavel_id)
                    .single();

                if (profileData) {
                    responsavelProfile = profileData;
                }
            }

            // A UNIQUE em analise_cadastral.negociacao_id faz o PostgREST devolver o embed
            // como OBJETO, e o DetailLeftColumn lê `analise_cadastral?.[0]`. Sem normalizar,
            // toda negociação reprovada por crédito aparecia como "sem decisão Reprovado":
            // sumiam o caminho do avalista e a declaração de não associação.
            setNegotiation({
                ...negData,
                analise_cadastral: embedToArray(negData.analise_cadastral),
                responsavel: responsavelProfile,
            });

            // Fetch UCs
            const { data: ucsData, error: ucsError } = await ucsPromise;
            if (ucsError) throw ucsError;
            setUcs(ucsData || []);

        } catch (error) {
            console.error('Error fetching details:', error);
        } finally {
            if (!silent) setLoading(false);
        }
    };

    const handleUpdate = () => {
        fetchData(true);
        onUpdate();
    };

    if (!isOpen || !negotiationId) return null;

    return createPortal(
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in p-4"
            {...propsFundoModal(() => onClose())}
        >
            <div className="bg-gray-100 dark:bg-slate-900 rounded-2xl w-full max-w-[95vw] h-[90vh] shadow-2xl overflow-hidden flex flex-col relative" onClick={e => e.stopPropagation()}>

                {/* Close Button absolute */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 z-50 p-2 bg-white/80 dark:bg-slate-800/80 hover:bg-white dark:hover:bg-slate-800 rounded-full text-gray-500 dark:text-slate-400 hover:text-gray-800 dark:hover:text-slate-200 transition-all shadow-sm"
                >
                    <X size={20} />
                </button>

                {loading ? (
                    <div className="flex-1 flex items-center justify-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                    </div>
                ) : negotiation ? (
                    <div className="flex flex-col h-full overflow-hidden">
                        {/* 1. Header Area */}
                        <div className="bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700/50 px-4 lg:px-6 py-4 shrink-0">
                            <DetailHeader negotiation={negotiation} onUpdate={handleUpdate} />
                        </div>

                        {/* 2. Main Grid Content */}
                        <div className="flex-1 overflow-y-auto lg:overflow-hidden flex flex-col lg:grid lg:grid-cols-12 gap-4 lg:gap-6 p-4 lg:p-6 custom-scrollbar">
                            {/* Left Column (25%) */}
                            <div className="lg:col-span-3 lg:overflow-y-auto custom-scrollbar lg:pr-2">
                                <TrocaDonoNegociacaoAviso negociacaoId={Number(negotiation.id)} onChange={handleUpdate} />
                                <IndicacaoNegociacaoCard negociacaoId={Number(negotiation.id)} onChange={handleUpdate} />
                                <DetailLeftColumn
                                    negotiation={negotiation}
                                    ucs={ucs}
                                    onUpdate={handleUpdate}
                                    onClose={onClose}
                                />
                            </div>

                            {/* Center Column (50%) */}
                            <div className="lg:col-span-6 lg:overflow-y-auto custom-scrollbar lg:px-2">
                                <DetailFeed
                                    negotiation={negotiation}
                                    onUpdate={handleUpdate}
                                    onClose={onClose}
                                />
                            </div>

                            {/* Right Column (25%) */}
                            <div className="lg:col-span-3 lg:overflow-y-auto custom-scrollbar lg:pl-2">
                                <DetailRightColumn
                                    negotiation={negotiation}
                                    ucs={ucs}
                                    onUpdate={handleUpdate}
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="flex-1 flex items-center justify-center text-gray-500 dark:text-slate-400">
                        Negociação não encontrada.
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
};

export default NegotiationDetailModal;
